import "./chatbot.css";
import React, { useState, useEffect, useRef, useCallback } from "react";
import minimizeIcon from "../../assets/images/minus-cirlce.svg";
import closeChatIcon from "../../assets/images/close-chat.svg";
import headerIcon from "../../assets/images/botIconLatest_defringed.png";
import { getBackendConfig, backendURL as defaultBackendURL, feedbackURL as defaultFeedbackURL } from "../../Configurations/config";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import FeedbackWidget from "./FeedbackWidget";
import ChatInput from "./ChatInput";

/** Safety net: strip any residual [UI:*] marker blocks (and stray tag lines) from
 *  prose. All rich UI now arrives on the structured SSE channel; this only guards
 *  against a stray marker leaking into the text during/after the migration. */
function stripUiMarkers(text) {
  if (!text) return "";
  return text
    .replace(/\[UI:[A-Z_]+\][\s\S]*?\[\/UI:[A-Z_]+\]/g, "")
    .replace(/^\s*\[\/?UI:[A-Z_]+\]\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n");
}

// DIVA is instructed to reply in plain text, but the LLM occasionally emits
// markdown (bold **, headers #, bullet lists). Normalize those markers to plain
// text; rendering escapes the text and creates explicit safe link nodes.
function stripMarkdown(text) {
  if (!text) return "";
  return text
    // headers: leading #'s on a line
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
    // bullet markers at line start (- * +) → bullet char, keeps the list readable
    .replace(/^[ \t]*[-*+][ \t]+/gm, "• ")
    // bold **text** / __text__ (underscore form guarded so it won't eat word__word)
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(^|[^_\w])__(?!\s)(.+?)(?<!\s)__(?![_\w])/g, "$1$2")
    // italic *text* / _text_ (won't touch snake_case or already-handled bullets)
    .replace(/(^|[^*])\*(?!\s)(.+?)(?<!\s)\*(?!\*)/g, "$1$2")
    .replace(/(^|[^_\w])_(?!\s)(.+?)(?<!\s)_(?![_\w])/g, "$1$2")
    // any leftover unbalanced bold marker (e.g. mid-stream, before the closing **)
    .replace(/\*\*/g, "");
}

const DIVA_SESSION_STORAGE_KEY = "diva_chat_session_id";

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const textAreaRef = useRef(null);
  const chatEndRef = useRef(null);
  const tokenSessionRef = useRef(null);
  const welcomeInFlightRef = useRef(false);
  const [typing, setTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [loader, setLoader] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [channelType, setChannelType] = useState("");
  const [agentAlias, setAgentAlias] = useState("");
  const [backendConfig, setBackendConfig] = useState(null);
  const [sessionConversation, setSessionConversation] = useState([]);
  const [frCode, setFrCode] = useState("");
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showIdleMessage, setShowIdleMessage] = useState(false);
  const [showSecondIdleMessage, setShowSecondIdleMessage] = useState(false);
  const [botHasResponded, setBotHasResponded] = useState(false);
  const [jwtToken, setJwtToken] = useState(null);
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem("diva_theme");
      if (stored === "light" || stored === "dark") return stored;
    } catch {
      // ignore storage access errors
    }
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  });

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem("diva_theme", next);
      } catch {
        // ignore storage access errors
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const channelType = urlParams.get("channel") || urlParams.get("channelType") || "web";
    const agentId =
      urlParams.get("agentId") ||
      (channelType === "channelpartners"
        ? "CPDIVA"
        : channelType === "mob"
          ? "DIVA_MOB"
          : "DIVA");
    const agentAlias = urlParams.get("alias") || "default";
    const frCode = urlParams.get("frCode") || "";

    if (frCode) {
      storeFrCode(frCode);
    }

    setAgentId(agentId);
    setChannelType(channelType);
    setAgentAlias(agentAlias);
    setFrCode(frCode);

    const config = getBackendConfig(channelType);
    setBackendConfig(config);
    console.log(`[CHATBOT] Channel: ${channelType}, Backend: ${config.backendURL}`);

    // Always start a fresh session on page load. Persisting the id in
    // sessionStorage made a hard refresh reuse the same session_id, so the
    // backend S3 session restored the prior (mid-transaction) conversation and
    // the bot resumed it instead of resetting. A new id per load = fresh state;
    // the beforeunload close-session beacon still cleans up the old session.
    const sid = uuidv4();
    setSessionId(sid);
  }, []);

  const storeFrCode = async (frCode) => {
    try {
      await fetch("https://r2scrbvcas.ap-south-1.awsapprunner.com/frcode", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ frcode: frCode }),
      });
    } catch (error) {
      console.error("Error storing frCode:", error);
    }
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [sessionConversation, loader, showFeedback]);

  const uuidv4 = () => {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (
        +c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
      ).toString(16)
    );
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (sessionId && backendConfig) {
        const blob = new Blob(
          [JSON.stringify({ session_id: sessionId, channelType })],
          { type: "application/json" }
        );
        navigator.sendBeacon(backendConfig.closeSessionURL, blob);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sessionId, channelType, backendConfig]);

  useEffect(() => {
    const fetchWelcomeMessage = async () => {
      if (!sessionId || !backendConfig || welcomeInFlightRef.current) return;
      if (tokenSessionRef.current === sessionId && jwtToken) return;

      welcomeInFlightRef.current = true;
      try {
        const welcomeURL = backendConfig.welcomeMessageURL;
        const response = await fetch(welcomeURL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            channel_type: channelType || "web",
            session_id: sessionId,
            ...(channelType === "channelpartners" && frCode ? { fr_code: frCode } : {}),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.token) {
            setJwtToken(data.token);
            tokenSessionRef.current = sessionId;
            console.log("[CHATBOT] JWT token obtained for session", sessionId);
          }
          if (data.message && sessionConversation.length === 0) {
            setSessionConversation([{ type: "SYSTEM", body: data.message, id: 0 }]);
          }
        } else {
          console.error("Failed to fetch welcome message:", response.status);
        }
      } catch (error) {
        console.error("Error fetching welcome message:", error);
        if (sessionConversation.length > 0) return;

        // Offline fallback (backend unreachable): one message per app.
        const fallbackBody =
          channelType === "channelpartners"
            ? `Hi, I'm DIVA! Welcome to Limitless. How can I help you today?`
            : `Hi, I'm DIVA! Welcome to DTDC. I'm here to help with tracking, serviceability, delivery times, rates, and more. How can I help you today?`;
        setSessionConversation([{ type: "SYSTEM", body: fallbackBody, id: 0 }]);
      } finally {
        welcomeInFlightRef.current = false;
      }
    };

    fetchWelcomeMessage();
  }, [sessionId, channelType, backendConfig, jwtToken, sessionConversation.length]);

  const refreshSessionToken = async (activeSessionId = sessionId) => {
    const welcomeURL = backendConfig
      ? backendConfig.welcomeMessageURL
      : defaultBackendURL.replace("get-dtdc-agent-response", "welcome-message");

    const tokenResponse = await fetch(welcomeURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        channel_type: channelType || "web",
        session_id: activeSessionId,
        ...(channelType === "channelpartners" && frCode ? { fr_code: frCode } : {}),
      }),
    });

    if (!tokenResponse.ok) {
      throw new Error("Failed to refresh session token");
    }

    const tokenData = await tokenResponse.json();
    if (!tokenData.token) {
      throw new Error("Failed to get new token");
    }

    setJwtToken(tokenData.token);
    tokenSessionRef.current = activeSessionId;
    return tokenData.token;
  };

  const createNewSession = () => {
    sessionStorage.removeItem(DIVA_SESSION_STORAGE_KEY);
    setTyping(false);
    setIsOpen(false);
    window.location.reload();
  };

  const minimizeBot = () => {
    window.parent.postMessage({ msg: "minimize" }, "*");
  };

  const closeBot = () => {
    window.parent.postMessage({ msg: "chatbotClose" }, "*");
    setIsOpen(!isOpen);
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const onClickHeaderIcon = async () => {
    if (sessionId && backendConfig) {
      try {
        await fetch(backendConfig.closeSessionURL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, channelType }),
        });
      } catch (error) {
        console.error("Error closing session:", error);
      }
    }
    setIsOpen(!isOpen);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (inputValue.trim() !== "") {
        handleSendMessage(event.target.value);
      }
    }
  };

  const sendRequest = () => {
    if (inputValue.trim() !== "") {
      handleSendMessage(inputValue);
    }
  };

  useEffect(() => {
    const idleTimer = setInterval(() => {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - lastActivityTime;

      if (!botHasResponded) return;

      if (
        timeSinceLastActivity >= 300000 && // 5 min (was 2 min)
        !showIdleMessage &&
        !showSecondIdleMessage &&
        !showFeedback &&
        !feedbackSubmitted &&
        !typing &&
        !loader &&
        !isStreaming
      ) {
        setShowIdleMessage(true);
        setSessionConversation((prev) => [
          ...prev,
          { type: "SYSTEM", body: "I see it's been a while. Is there anything else I can assist you with?" },
        ]);
      }

      if (
        timeSinceLastActivity >= 360000 && // 6 min — kept the same 1 min gap after the first message
        showIdleMessage &&
        !showSecondIdleMessage &&
        !showFeedback &&
        !feedbackSubmitted &&
        !typing &&
        !loader &&
        !isStreaming
      ) {
        setShowSecondIdleMessage(true);
        setShowFeedback(true);
      }
    }, 1000);

    return () => clearInterval(idleTimer);
  }, [lastActivityTime, showIdleMessage, showSecondIdleMessage, showFeedback, feedbackSubmitted, typing, loader, isStreaming, botHasResponded]);

  const updateLastActivityTime = () => {
    setLastActivityTime(Date.now());
    if (!feedbackSubmitted) {
      setShowIdleMessage(false);
      setShowSecondIdleMessage(false);
      setShowFeedback(false);
    }
  };

  const handleFeedbackSubmission = useCallback(async (feedbackText, rating) => {
    const apiURL = backendConfig ? backendConfig.feedbackURL : defaultFeedbackURL;
    console.log(`[CHATBOT] Submitting feedback to: ${apiURL}`);

    const urlParams = new URLSearchParams(window.location.search);
    const agentIdPayload =
      urlParams.get("agentId") ||
      (channelType === "channelpartners"
        ? "CPDIVA"
        : channelType === "mob"
          ? "DIVA_MOB"
          : "DIVA");
    const frCodePayload = urlParams.get("frCode") || "";

    try {
      const payload = {
        session_id: sessionId,
        feedback: rating >= 4 ? "positive" : "negative",
        rating,
        comment: feedbackText,
        channelType,
        agentid: agentIdPayload,
      };
      if (frCodePayload) {
        payload.frcode = frCodePayload;
      }

      const response = await fetch(apiURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setFeedbackSubmitted(true);
        setShowFeedback(false);
        setSessionConversation((prev) => [...prev, {
          type: "SYSTEM",
          body: `Thank you for your ${rating}-star rating and feedback. I'll work on improving my responses.`,
        }]);
        setTimeout(() => {
          setSessionId("");
          sessionStorage.removeItem(DIVA_SESSION_STORAGE_KEY);
          window.location.reload();
        }, 5000);
      } else {
        throw new Error("Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      return false;
    }
  }, [backendConfig, sessionId, channelType]);

  const handleSendMessage = async (query) => {
    if (showFeedback || feedbackSubmitted) return;
    updateLastActivityTime();
    setLoader(true);
    setTyping(true);
    setIsStreaming(false);
    setInputValue("");

    const messageId = Date.now();

    setSessionConversation((prev) => [
      ...prev,
      { type: "USER", body: query, id: messageId },
    ]);

    const request = {
      user_input: query,
      session_id: sessionId,
      agentId,
      channelType,
      agentAlias,
      frCode,
    };

    const apiURL = backendConfig ? backendConfig.backendURL : defaultBackendURL;
    console.log(`[CHATBOT] Sending request to: ${apiURL}`);

    // Abort the stream if no bytes arrive for this long. The backend sends an
    // SSE keep-alive comment every ~15s, so a healthy stream keeps resetting
    // this timer; only a genuinely dead connection trips it.
    const STREAM_INACTIVITY_MS = 45000;
    const controller = new AbortController();
    let inactivityTimer = null;
    let streamProducedOutput = false;
    const resetInactivityTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        console.warn("[CHATBOT] Stream inactive — aborting request");
        controller.abort();
      }, STREAM_INACTIVITY_MS);
    };
    const clearInactivityTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        inactivityTimer = null;
      }
    };

    try {
      let authToken = jwtToken;
      if (!authToken || tokenSessionRef.current !== sessionId) {
        authToken = await refreshSessionToken(sessionId);
      }

      const postAgentRequest = (token) =>
        fetch(apiURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });

      let response = await postAgentRequest(authToken);

      if (response.status === 401 || response.status === 403) {
        console.log(`[CHATBOT] Auth rejected (${response.status}), refreshing token...`);
        authToken = await refreshSessionToken(sessionId);
        response = await postAgentRequest(authToken);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      try {
        const headerSnapshot = {
          "content-type": response.headers.get("content-type"),
          "content-length": response.headers.get("content-length"),
          "transfer-encoding": response.headers.get("transfer-encoding"),
          "content-encoding": response.headers.get("content-encoding"),
          "cache-control": response.headers.get("cache-control"),
        };
        console.log("[CHATBOT] Stream response headers:", headerSnapshot);
      } catch {
        // ignore
      }

      if (!response.body) {
        const fallbackText = await response.text();
        setLoader(false);
        setTyping(false);
        setIsStreaming(false);
        setSessionConversation((prev) => [
          ...prev,
          { type: "SYSTEM", body: fallbackText || "", id: messageId + 1 },
        ]);
        setBotHasResponded(true);
        updateLastActivityTime();
        return;
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      const isSseResponse = contentType.includes("text/event-stream");

      if (!isSseResponse) {
        console.warn(
          `[CHATBOT] Response is not text/event-stream (content-type: ${contentType || "<missing>"}). Streaming may be buffered by an upstream proxy/gateway.`
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let ongoingBotResponse = "";
      let buffer = "";
      const structuredPayloads = [];
      const botMessageId = messageId + 1;

      // Create or update the bot message from prose (body) + structured cards.
      // All rich UI now rides parsed.structured; body is plain prose only.
      const upsertBotMessage = (body) => {
        setLoader(false);
        setIsStreaming(true);
        setSessionConversation((prev) => {
          const next = [...prev];
          const idx = next.findIndex((msg) => msg.id === botMessageId);
          const structured = [...structuredPayloads];
          if (idx === -1) {
            next.push({ type: "SYSTEM", body, structured, id: botMessageId });
          } else {
            next[idx] = { ...next[idx], body, structured };
          }
          return next;
        });
      };

      const updateUI = (response) => {
        const body = stripMarkdown(stripUiMarkers(response.replace(/^\n+/, "")));
        if (body.trim() || structuredPayloads.length) {
          upsertBotMessage(body);
        }
      };

      const parseSseEvent = (rawEvent) => {
        const lines = rawEvent.split("\n");
        let eventType = "message";
        let eventId;
        let retry;
        const dataLines = [];

        for (const line of lines) {
          if (!line) continue;
          if (line.startsWith(":")) continue;

          const colonIndex = line.indexOf(":");
          const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
          const value = colonIndex === -1 ? "" : line.slice(colonIndex + 1).replace(/^\s/, "");

          if (field === "event") eventType = value;
          else if (field === "id") eventId = value;
          else if (field === "retry") retry = value;
          else if (field === "data") dataLines.push(value);
        }

        return { eventType, id: eventId, retry, data: dataLines.join("\n") };
      };

      const processStream = async () => {
        const flushFinalUi = () => {
          updateUI(ongoingBotResponse);
        };

        const handleParsedData = (parsed) => {
          if (parsed == null) return;

          if (typeof parsed === "string") {
            if (parsed === "[DONE]") return "done";
            ongoingBotResponse += parsed;
          } else {
            if (parsed.message) {
              ongoingBotResponse += parsed.message;
            } else if (parsed.response) {
              ongoingBotResponse += parsed.response;
            }
            if (parsed.frCode !== undefined) {
              setFrCode(parsed.frCode);
            }
            if (parsed.structured) {
              structuredPayloads.push(parsed.structured);
            }
            if (parsed.data === "[DONE]") return "done";
          }

          if (ongoingBotResponse.trim() || structuredPayloads.length) {
            streamProducedOutput = true;
          }

          updateUI(ongoingBotResponse);

          return undefined;
        };

        resetInactivityTimer();
        while (true) {
          const { done, value } = await reader.read();
          resetInactivityTimer();
          if (done) {
            clearInactivityTimer();
            const remaining = buffer.trim();
            if (remaining) {
              const normalizedRemaining = remaining.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
              if (isSseResponse) {
                const evt = parseSseEvent(normalizedRemaining);
                const evtData = (evt.data || "").trim();
                if (evtData && evtData !== "[DONE]") {
                  try {
                    handleParsedData(JSON.parse(evtData));
                  } catch {
                    handleParsedData(evtData);
                  }
                }
              } else {
                try {
                  handleParsedData(JSON.parse(normalizedRemaining));
                } catch {
                  handleParsedData(normalizedRemaining);
                }
              }
            }

            flushFinalUi();

            setLoader(false);
            setTyping(false);
            setIsStreaming(false);
            setBotHasResponded(true);
            updateLastActivityTime();
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          buffer = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

          while (true) {
            const boundaryIndex = buffer.indexOf("\n\n");
            if (boundaryIndex === -1) break;

            const rawEvent = buffer.slice(0, boundaryIndex);
            buffer = buffer.slice(boundaryIndex + 2);

            if (!rawEvent.trim()) continue;

            const evt = parseSseEvent(rawEvent);
            const evtData = (evt.data || "").trim();
            if (!evtData) continue;

            if (evtData === "[DONE]") {
              flushFinalUi();
              setLoader(false);
              setTyping(false);
              setIsStreaming(false);
              setBotHasResponded(true);
              updateLastActivityTime();
              return;
            }

            let parsed;
            try {
              parsed = JSON.parse(evtData);
            } catch {
              parsed = evtData;
            }

            const result = handleParsedData(parsed);
            if (result === "done") {
              flushFinalUi();
              setLoader(false);
              setTyping(false);
              setIsStreaming(false);
              setBotHasResponded(true);
              updateLastActivityTime();
              return;
            }
          }
        }

        updateUI(ongoingBotResponse);
        setLoader(false);
        setTyping(false);
        setIsStreaming(false);
        setBotHasResponded(true);
        updateLastActivityTime();
      };

      await processStream();
      clearInactivityTimer();
    } catch (error) {
      clearInactivityTimer();
      const aborted = error?.name === "AbortError";
      console.error(
        aborted ? "Stream aborted (inactivity/timeout)" : "Error fetching streamed response:",
        error
      );
      setLoader(false);
      setTyping(false);
      setIsStreaming(false);
      if (!streamProducedOutput) {
        setSessionConversation((prev) => [
          ...prev,
          {
            type: "SYSTEM",
            body: "I'm having trouble responding right now. Please resend your message.",
            id: Date.now(),
          },
        ]);
      }
      setBotHasResponded(true);
      updateLastActivityTime();
    }
  };

  return (
    <div className="chatbot-shell" data-theme={theme}>
      <ChatHeader
        channelType={channelType}
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        headerIcon={headerIcon}
        minimizeIcon={minimizeIcon}
        closeChatIcon={closeChatIcon}
        theme={theme}
        onToggleTheme={toggleTheme}
        onMinimize={minimizeBot}
        onClose={onClickHeaderIcon}
        onNewSession={createNewSession}
        onCloseBot={closeBot}
      />
      <MessageList
        sessionConversation={sessionConversation}
        loader={loader}
        isStreaming={isStreaming}
        chatEndRef={chatEndRef}
        headerIcon={headerIcon}
        channelType={channelType}
        onSelectPrompt={handleSendMessage}
        onWidgetSubmit={handleSendMessage}
        feedbackWidget={showFeedback ? <FeedbackWidget onSubmit={handleFeedbackSubmission} onCancel={updateLastActivityTime} /> : null}
      />
      <ChatInput
        showFeedback={showFeedback || feedbackSubmitted}
        inputValue={inputValue}
        typing={typing}
        textAreaRef={textAreaRef}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        onSend={sendRequest}
      />
    </div>
  );
}

export default Chatbot;
